import { StatusBadge } from "@/components/status-badge";
import { OrderStatusDialog } from "@/components/tables/_components/order-status-dialog";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { OrderService } from "@/services/order-services";
import { useMutation } from "@tanstack/react-query";
import {
  Link,
  createFileRoute,
  notFound,
  useCanGoBack,
  useRouter,
} from "@tanstack/react-router";
import { AlertTriangle, ChevronLeft, Pencil, Truck } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import OrderTabContent from "./-components/order-tabs-content";

export const Route = createFileRoute("/_main/orders/$orderId")({
  component: RouteComponent,
  loader: async ({ params }) => {
    const order = await OrderService.getOrderById(params.orderId);

    if (!order) {
      throw notFound();
    }

    return order;
  },
  notFoundComponent: () => {
    return (
      <div className="flex min-h-screen items-center justify-center bg-soft-gray p-4">
        <div className="w-full max-w-md space-y-6 rounded-xl bg-white p-8 text-center">
          <AlertTriangle
            className="mx-auto mb-4 h-16 w-16 text-neutral-gray"
            strokeWidth={1.5}
          />
          <h1 className="mb-2 font-bold text-3xl text-dark-purple">
            Order Not Found
          </h1>
          <p className="mb-4 text-cool-gray">
            We couldn't locate the order details you're looking for. The order
            might have been deleted or the link is incorrect.
          </p>
          <Link
            to="/orders"
            className="inline-block rounded-lg bg-soft-purple px-6 py-3 text-dark-purple transition-colors hover:bg-soft-purple/90"
          >
            Return to Orders
          </Link>
        </div>
      </div>
    );
  },
});

function RouteComponent() {
  const { orderId } = Route.useParams();
  const [activeTab, setActiveTab] = useState("order_details");
  const [isEditMode, setIsEditMode] = useState(false);
  const order = Route.useLoaderData();

  const router = useRouter();
  const canGoBack = useCanGoBack();

  const { mutate: updateOrder, isPending: isUpdating } = useMutation({
    mutationKey: ["update-order", order?.id],
    mutationFn: OrderService.updateOrder,
    onSuccess: () => {
      toast.success("Order updated successfully");
      router.invalidate();
    },
    onError: () => {
      toast.error("Failed to update order");
    },
  });

  if (!order) {
    router.navigate({ to: "/orders" });
  }

  const handleTabChange = (value: string) => {
    setActiveTab(value);
  };

  return (
    <div>
      <Button
        variant="link"
        className="mt-8 ml-4"
        onClick={() => router.history.back()}
        disabled={!canGoBack}
      >
        <ChevronLeft />
        Go back
      </Button>

      <div className="max-w-7xl p-4">
        <div className="mb-8 flex items-center gap-2">
          <h4 className="font-medium text-3xl">Edit Order #{orderId}</h4>
          <StatusBadge
            status={order.status}
            variant={
              order.status === "Pending"
                ? "pending"
                : order.status === "Approved"
                  ? "success"
                  : "error"
            }
          />
        </div>

        <Tabs
          defaultValue="order_details"
          value={activeTab}
          onValueChange={handleTabChange}
          className="custom-tabs w-full"
        >
          <div className="flex flex-col md:flex-row md:justify-between">
            <TabsList className="mb-4 flex w-full justify-start overflow-x-auto border-gray-200 border-b bg-transparent md:mb-0 md:w-auto">
              <TabsTrigger
                value="order_details"
                className="!rounded-none relative border-b-2 border-b-transparent px-4 py-2 text-gray-600 data-[state=active]:border-b-2 data-[state=active]:border-b-black data-[state=active]:font-medium data-[state=active]:text-black data-[state=active]:shadow-none"
              >
                Order Details
              </TabsTrigger>
              <TabsTrigger
                value="customer_info"
                className="!rounded-none relative border-b-2 border-b-transparent px-4 py-2 text-gray-600 data-[state=active]:border-b-2 data-[state=active]:border-b-black data-[state=active]:font-medium data-[state=active]:text-black data-[state=active]:shadow-none"
                disabled={true}
              >
                Customer Info
              </TabsTrigger>
              <TabsTrigger
                value="delivery"
                className="!rounded-none relative border-b-2 border-b-transparent px-4 py-2 text-gray-600 data-[state=active]:border-b-2 data-[state=active]:border-b-black data-[state=active]:font-medium data-[state=active]:text-black data-[state=active]:shadow-none"
                disabled={true}
              >
                Delivery
              </TabsTrigger>
            </TabsList>
            {!isEditMode && (
              <div className="mt-4 flex gap-2 md:mt-0">
                <Button
                  variant="outline"
                  onClick={() => setIsEditMode(true)}
                  disabled={isEditMode || order.status !== "Pending"}
                  className="flex items-center gap-2"
                >
                  <Pencil className="h-4 w-4" />
                  <span>Edit</span>
                </Button>

                <OrderStatusDialog
                  order={order}
                  onSave={(id, data) => updateOrder({ id, data })}
                  trigger={
                    <Button
                      variant="default"
                      disabled={order.status !== "Pending"}
                    >
                      Process Order
                      <Truck className="h-4 w-4" />
                    </Button>
                  }
                />
              </div>
            )}
          </div>

          <TabsContent value="order_details" className="mt-6">
            <OrderTabContent
              activeTab="order_details"
              isEditMode={isEditMode}
              onEditModeChange={setIsEditMode}
            />
          </TabsContent>

          <TabsContent value="customer_info" className="mt-6">
            <OrderTabContent
              activeTab="customer_info"
              isEditMode={isEditMode}
              onEditModeChange={setIsEditMode}
            />
          </TabsContent>

          <TabsContent value="delivery" className="mt-6">
            <OrderTabContent
              activeTab="delivery"
              isEditMode={isEditMode}
              onEditModeChange={setIsEditMode}
            />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
